import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { api, CATEGORY_LABEL } from "@/lib/api";
import { API } from "@/lib/auth";
import { useLiveEvents } from "@/lib/events";
import { PageHeader, Table, Btn, Modal, Input, Select, Badge, Textarea, Card } from "@/components/UI";
import RawMaterialCardModal from "@/components/RawMaterialCard";
import BulkDocumentUploadModal from "@/components/BulkDocumentUploadModal";
import BoxLabelPreview from "@/components/BoxLabelPreview";
import CameraCapture from "@/components/CameraCapture";
import { LabelPhotoStrip } from "@/components/PhotoModal";
import LotLocationsDialog from "@/components/LotLocationsDialog";
import { Plus, PackagePlus, ShieldCheck, History, X, Upload, Download, AlertTriangle, FileSpreadsheet, ChevronRight, Filter, Tag, Camera, Image as ImageIcon, Loader2, FileText, Clock, ArrowDownCircle, ArrowUpCircle, Bookmark, Pencil, Trash2 } from "lucide-react";
import { openPdfPreview } from "@/lib/pdfPreview";
import { FilterAndReportBar } from "@/components/FilterAndReportBar";
import MaterialsHubPage from "@/pages/MaterialsHub";
import { usePagination, ListPagination } from "@/components/ListPagination";

const emptyMat = { name: "", category: "raw_material", unit: "kg", critical_stock: 0, lead_time_days: 7, unit_cost: 0, currency: "EUR", vat_rate: 20, active: true, initial_stock: 0, initial_supplier: "" };
// Iter242 — Canonical unit selection for new/edit forms only.
const CANONICAL_UNITS = ["kg", "adet"];

/**
 * ErrorTooltip — hover/tap on ⚠ shows all validation errors from backend.
 * Errors come as [{field, message}, ...] on material.validation_errors.
 */
function ErrorTooltip({ material, testid }) {
  const [open, setOpen] = useState(false);
  const errs = material.validation_errors || [];
  if (errs.length === 0) return null;
  const excel = material.excel_row_number ? `Excel Satırı ${material.excel_row_number}` : null;
  return (
    <span className="relative inline-flex" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        onClick={() => setOpen(o => !o)}
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${material.has_critical_errors ? "bg-red-100 text-red-700 border border-red-300" : "bg-amber-100 text-amber-700 border border-amber-300"}`}
        data-testid={testid || `mat-warn-${material.id}`}
        aria-label="Hata detayı"
      >
        <AlertTriangle size={12} />
      </button>
      {open && (
        <div className="absolute z-50 left-6 top-0 bg-white border border-slate-200 shadow-lg rounded p-3 min-w-64 max-w-md text-xs" data-testid={`mat-warn-tooltip-${material.id}`}>
          {excel && <div className="text-slate-500 mb-1 font-semibold">{excel}</div>}
          {errs.length > 1 && (
            <div className="text-slate-700 mb-1">Bu kayıtta {errs.length} doğrulama sorunu bulunmaktadır:</div>
          )}
          <ul className="space-y-1">
            {errs.map((e, i) => (
              <li key={i} className="flex items-start gap-1">
                <span className="text-red-500">⚠</span>
                <span className="text-slate-700">{e.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </span>
  );
}

/**
 * ImportWizard — 3-adım modal: Yükle → Ön İzleme → İçeri Aktar.
 * Backend endpoints: /inventory/import/preview, /inventory/import/execute.
 */
function ImportWizard({ open, onClose, onImported }) {
  const [phase, setPhase] = useState("upload"); // upload | preview | done
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // { rows, summary, file_name }
  const [dupAction, setDupAction] = useState("update"); // iter313: default smart update
  const [result, setResult] = useState(null);

  const reset = () => { setPhase("upload"); setFile(null); setPreview(null); setResult(null); setDupAction("update"); };

  const handleUpload = async () => {
    if (!file) { alert("Lütfen bir .xlsx dosyası seçin"); return; }
    if (!file.name.toLowerCase().endsWith(".xlsx")) { alert("Yalnızca .xlsx dosyaları desteklenir"); return; }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/inventory/import/preview`, form, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data);
      setPhase("preview");
    } catch (e) {
      alert(e.response?.data?.detail || "Ön izleme alınamadı");
    } finally { setBusy(false); }
  };

  const handleExecute = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const payload = { file_name: preview.file_name, rows: preview.rows, duplicate_action: dupAction };
      const res = await api.post("/inventory/import/execute", payload);
      setResult(res);
      setPhase("done");
      onImported?.();
    } catch (e) {
      alert(e.response?.data?.detail || "İçeri aktarım başarısız");
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    await api.download(`/inventory/import/template`, `Envanter_Import_Sablonu.xlsx`);
  };

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} title="Excel ile Envantere Toplu Aktarım" size="xl">
      {/* Stepper */}
      <div className="flex items-center gap-2 text-xs mb-4">
        {[["upload", "1 · Dosya Yükle"], ["preview", "2 · Ön İzleme"], ["done", "3 · Tamamlandı"]].map(([k, l], i, arr) => (
          <React.Fragment key={k}>
            <span className={`px-3 py-1 rounded-full font-semibold ${phase === k ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-500"}`} data-testid={`imp-step-${k}`}>{l}</span>
            {i < arr.length - 1 && <ChevronRight size={14} className="text-slate-300"/>}
          </React.Fragment>
        ))}
      </div>

      {phase === "upload" && (
        <div className="space-y-4">
          <div className="text-xs bg-teal-50 border border-teal-200 rounded p-3 text-teal-800">
            <div className="font-semibold mb-1">📋 Nasıl Çalışır?</div>
            <ol className="list-decimal ml-4 space-y-1">
              <li>Önce Excel şablonunu indirin, malzemeleri doldurun (Ana Kategori: Hammadde / Ambalaj).</li>
              <li>Dolu dosyayı yüklediğinizde sistem hataları önceden gösterir — DB'ye HENÜZ yazılmaz.</li>
              <li>Ön izlemede onaylarsanız, okunabilir satırlar Envantere alınır; kritik hatalıysalar ⚠ ile işaretlenir.</li>
              <li>Malzeme kodları (MAT-xxxxx) sistem tarafından otomatik üretilir.</li>
            </ol>
          </div>
          <div className="flex justify-center">
            <Btn variant="secondary" onClick={downloadTemplate} data-testid="imp-download-template"><Download size={14}/> Excel Şablonunu İndir</Btn>
          </div>
          <label className="block border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-teal-500 cursor-pointer" data-testid="imp-dropzone">
            <input type="file" accept=".xlsx" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="imp-file-input" />
            <Upload size={28} className="mx-auto text-slate-400 mb-2"/>
            <div className="text-sm text-slate-700">{file ? file.name : "Excel dosyasını buraya sürükleyin veya seçmek için tıklayın"}</div>
            <div className="text-xs text-slate-400 mt-1">Yalnızca .xlsx</div>
          </label>
          <div className="flex justify-end gap-2">
            <Btn variant="secondary" onClick={() => { reset(); onClose(); }}>Vazgeç</Btn>
            <Btn onClick={handleUpload} disabled={!file || busy} data-testid="imp-parse-btn"><FileSpreadsheet size={14}/> {busy ? "Okunuyor..." : "Ön İzlemeye Geç"}</Btn>
          </div>
        </div>
      )}

      {phase === "preview" && preview && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs" data-testid="imp-preview-summary">
            <SummaryCard label="Toplam Satır" value={preview.summary.total} color="slate"/>
            <SummaryCard label="Yeni Malzeme" value={preview.summary.material_create || 0} color="emerald" testid="sm-mat-create"/>
            <SummaryCard label="Malzeme Güncelle" value={preview.summary.material_update || 0} color="indigo" testid="sm-mat-update"/>
            <SummaryCard label="Yeni Lot" value={preview.summary.lot_create || 0} color="teal" testid="sm-lot-create"/>
            <SummaryCard label="Lot Güncelle" value={preview.summary.lot_update || 0} color="amber" testid="sm-lot-update"/>
            <SummaryCard label="Hatalı / Atlanacak" value={(preview.summary.not_importable || 0)} color="red" testid="sm-fail"/>
          </div>
          {preview.summary.duplicates > 0 && (
            <div className="bg-indigo-50 border border-indigo-200 rounded p-2 text-xs flex items-center gap-2" data-testid="imp-dup-action-bar">
              <span className="text-indigo-800 font-semibold">Mevcut Malzemelerde ({preview.summary.duplicates}):</span>
              <Select value={dupAction} onChange={e => setDupAction(e.target.value)}>
                <option value="update">Akıllı Güncelle (stok+SKT replace, aynı lot update, yeni lot ekle)</option>
                <option value="skip">Atla</option>
                <option value="create_new">Yeni Kayıt Olarak Oluştur (duplicate)</option>
              </Select>
            </div>
          )}
          <div className="max-h-[420px] overflow-auto border rounded">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 sticky top-0"><tr>
                <th className="text-left px-2 py-1.5">#</th>
                <th className="text-left px-2 py-1.5">Malzeme</th>
                <th className="text-left px-2 py-1.5">Ana Kat.</th>
                <th className="text-left px-2 py-1.5">Alt Kat.</th>
                <th className="text-left px-2 py-1.5">Birim</th>
                <th className="text-right px-2 py-1.5">Krit. Stok</th>
                <th className="text-right px-2 py-1.5">Birim Maliyet</th>
                <th className="text-right px-2 py-1.5">Başl. Stok</th>
                <th className="text-left px-2 py-1.5">Lot</th>
                <th className="text-left px-2 py-1.5">SKT</th>
                <th className="text-left px-2 py-1.5">Kontrol</th>
              </tr></thead>
              <tbody>
                {preview.rows.map((r) => {
                  const errs = r.validation_errors || [];
                  const bg = !r.importable ? "bg-slate-100" : r.duplicate_of ? "bg-indigo-50/40" : errs.length > 0 ? (r.has_critical_errors ? "bg-red-50/50" : "bg-amber-50/50") : "";
                  return (
                    <tr key={r.excel_row_number} className={`border-b ${bg}`} data-testid={`imp-row-${r.excel_row_number}`}>
                      <td className="px-2 py-1 font-mono text-slate-500">{r.excel_row_number}</td>
                      <td className="px-2 py-1">{r.name || <span className="text-red-500 italic">(boş)</span>}</td>
                      <td className="px-2 py-1">{r.main_kind === "raw" ? "Hammadde" : r.main_kind === "packaging" ? "Ambalaj" : <span className="text-slate-400">—</span>}</td>
                      <td className="px-2 py-1">{r.category ? (CATEGORY_LABEL[r.category] || r.category) : <span className="text-slate-400">—</span>}</td>
                      <td className="px-2 py-1">{r.unit || <span className="text-slate-400">—</span>}</td>
                      <td className="px-2 py-1 text-right font-mono">{r.critical_stock ?? "—"}</td>
                      <td className="px-2 py-1 text-right font-mono">{r.unit_cost != null ? `${r.unit_cost} ${r.currency || ""}` : "—"}</td>
                      <td className="px-2 py-1 text-right font-mono">{r.initial_stock ?? "—"}</td>
                      <td className="px-2 py-1 font-mono">{r.lot_no || "—"}</td>
                      <td className="px-2 py-1 font-mono">{r.expiry_date || "—"}</td>
                      <td className="px-2 py-1">
                        {!r.importable ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-slate-200 text-slate-700 rounded px-2 py-0.5" data-testid={`imp-row-${r.excel_row_number}-status`}>
                            <X size={11}/> Satır İçe Aktarılamadı
                          </span>
                        ) : r.duplicate_of ? (
                          <span className="inline-flex items-center gap-1 text-xs bg-indigo-100 text-indigo-800 rounded px-2 py-0.5" data-testid={`imp-row-${r.excel_row_number}-status`}>
                            🔍 Benzer: {r.duplicate_of.code}
                          </span>
                        ) : errs.length > 0 ? (
                          <ErrorTooltip material={{ ...r, id: `preview-${r.excel_row_number}`, has_critical_errors: r.has_critical_errors }} testid={`imp-row-${r.excel_row_number}-warn`}/>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-800 rounded px-2 py-0.5" data-testid={`imp-row-${r.excel_row_number}-status`}>✓ Hazır</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex justify-end gap-2">
            <Btn variant="secondary" onClick={() => setPhase("upload")}>Geri</Btn>
            <Btn onClick={handleExecute} disabled={busy || !preview.rows?.some(r => r.name)} data-testid="imp-execute-btn"><Upload size={14}/> {busy ? "Aktarılıyor..." : `İçeri Aktar (${preview.summary.importable})`}</Btn>
          </div>
        </div>
      )}

      {phase === "done" && result && (
        <div className="space-y-3" data-testid="imp-done-panel">
          <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
            <div className="text-sm font-semibold text-emerald-800">✅ Import Tamamlandı — Batch: <span className="font-mono">{result.batch_no}</span></div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <SummaryCard label="Toplam" value={result.summary.total_rows} color="slate"/>
            <SummaryCard label="Oluşturuldu" value={result.summary.created} color="emerald"/>
            <SummaryCard label="Uyarılı" value={result.summary.with_warnings} color="amber"/>
            <SummaryCard label="Atlandı" value={result.summary.skipped} color="slate"/>
            <SummaryCard label="Hatalı" value={result.summary.failed} color="red"/>
          </div>
          {(result.skipped || []).length > 0 && (
            <div className="text-xs">
              <div className="font-semibold mb-1 text-slate-600">Atlanan Satırlar:</div>
              <ul className="space-y-0.5">
                {result.skipped.map((s, i) => <li key={i}>• Satır {s.excel_row_number}: {s.reason}</li>)}
              </ul>
            </div>
          )}
          <div className="flex justify-end">
            <Btn onClick={() => { reset(); onClose(); }} data-testid="imp-close-btn">Kapat & Envantere Dön</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

function SummaryCard({ label, value, color = "slate" }) {
  const map = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    red: "bg-red-50 text-red-700 border-red-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  };
  return (
    <div className={`border rounded p-2 ${map[color] || map.slate}`}>
      <div className="text-[10px] uppercase opacity-75">{label}</div>
      <div className="text-lg font-mono font-bold">{value}</div>
    </div>
  );
}

// iter192 — Bulk Allergen Import from Excel
function BulkAllergenImportModal({ open, onClose }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [applied, setApplied] = useState(null);

  const reset = () => { setFile(null); setPreview(null); setApplied(null); };

  const downloadTemplate = async () => {
    await api.download("/raw-material-card-tools/allergen-import-template",
      "allergen_toplu_import_sablonu.xlsx");
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true); setApplied(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("preview", "true");
      const res = await axios.post(
        `${API}/raw-material-card-tools/import-allergens`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Ön izleme alınamadı");
    } finally { setBusy(false); }
  };

  const doApply = async () => {
    if (!file || !preview) return;
    if (!window.confirm(`${preview.summary.rows} satır uygulanacak (${preview.summary.materials} malzeme). Devam?`)) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("preview", "false");
      const res = await axios.post(
        `${API}/raw-material-card-tools/import-allergens`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      setApplied(res.data);
    } catch (e) {
      alert(e.response?.data?.detail || "İçeri aktarım başarısız");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }}
           title="Toplu Alerjen İçe Aktarım (Excel)" size="xl">
      <div className="space-y-3" data-testid="bulk-allergen-modal">
        <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
          <b>Fragrance / SDS tedarikçilerinden gelen Excel dosyasını yükleyin.</b>
          Sistem hangi Hammadde Kartına hangi alerjen kaydının yazılacağını
          <span className="font-mono px-1">material_code</span> ile eşler.
          <b> Mevcut verified=true kayıtlar korunur</b> (Iter191 merge semantiği).
          Kaynak (source_url veya source_reference) her satırda zorunludur.
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Btn variant="secondary" onClick={downloadTemplate}
               data-testid="bulk-allergen-template-btn">
            <Download size={14}/> Excel Şablonu İndir
          </Btn>
          <input type="file" accept=".xlsx"
                 onChange={e => { setFile(e.target.files[0]); setPreview(null); setApplied(null); }}
                 data-testid="bulk-allergen-file"
                 className="text-xs"/>
          <Btn onClick={runPreview} disabled={!file || busy}
               data-testid="bulk-allergen-preview-btn">
            <FileSpreadsheet size={14}/> {busy && !preview ? "Okunuyor..." : "Önizle (Dry-run)"}
          </Btn>
          {preview && preview.summary.rows > 0 && !applied && (
            <Btn onClick={doApply} disabled={busy}
                 data-testid="bulk-allergen-apply-btn">
              <Upload size={14}/> {busy ? "Uygulanıyor..." : `Uygula (${preview.summary.rows} satır)`}
            </Btn>
          )}
        </div>

        {preview && (
          <div className="space-y-2">
            <div className="grid grid-cols-4 gap-2 text-xs">
              <Card className="p-2 bg-emerald-50 border-emerald-200">
                <div className="text-slate-500">Geçerli Satır</div>
                <div className="text-xl font-bold text-emerald-700" data-testid="bulk-summary-rows">{preview.summary.rows}</div>
              </Card>
              <Card className="p-2 bg-indigo-50 border-indigo-200">
                <div className="text-slate-500">Malzeme</div>
                <div className="text-xl font-bold text-indigo-700">{preview.summary.materials}</div>
              </Card>
              <Card className="p-2 bg-rose-50 border-rose-200">
                <div className="text-slate-500">Hata / Uyarı</div>
                <div className="text-xl font-bold text-rose-700" data-testid="bulk-summary-errors">{preview.summary.errors}</div>
              </Card>
              <Card className="p-2 bg-slate-50 border-slate-200">
                <div className="text-slate-500">Sheet</div>
                <div className="text-sm font-mono">{preview.sheet || "—"}</div>
              </Card>
            </div>

            {(preview.materials || []).length > 0 && (
              <div className="border rounded overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="p-1.5 text-left">Malzeme Kodu</th>
                      <th className="p-1.5 text-left">Malzeme Adı</th>
                      <th className="p-1.5 text-right">Toplam Satır</th>
                      <th className="p-1.5 text-right">Doğrulandı</th>
                      {applied && <th className="p-1.5 text-right">Uygulandı / Card Total</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {(applied ? applied.materials : preview.materials).map((m, i) => (
                      <tr key={i} className="border-t" data-testid={`bulk-mat-${m.material_code}`}>
                        <td className="p-1.5 font-mono">{m.material_code}</td>
                        <td className="p-1.5">{m.material_name}</td>
                        <td className="p-1.5 text-right font-mono">{m.rows}</td>
                        <td className="p-1.5 text-right font-mono">{m.verified_rows}</td>
                        {applied && <td className="p-1.5 text-right font-mono text-emerald-700">{m.applied} / {m.total}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(preview.row_errors || []).length > 0 && (
              <div className="border border-rose-200 rounded overflow-hidden">
                <div className="px-2 py-1 bg-rose-50 text-rose-800 text-xs font-semibold">
                  Hatalar / Uyarılar ({preview.row_errors.length})
                </div>
                <table className="w-full text-xs">
                  <thead className="bg-rose-100">
                    <tr>
                      <th className="p-1.5 text-left">Satır</th>
                      <th className="p-1.5 text-left">Malzeme</th>
                      <th className="p-1.5 text-left">INCI</th>
                      <th className="p-1.5 text-left">Sorun</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.row_errors.slice(0, 80).map((e, i) => (
                      <tr key={i} className="border-t">
                        <td className="p-1.5 font-mono">{e.row_no}</td>
                        <td className="p-1.5 font-mono">{e.material_code || "—"}</td>
                        <td className="p-1.5">{e.inci_name || "—"}</td>
                        <td className="p-1.5 text-rose-700">{e.issue}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {applied && (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-900" data-testid="bulk-applied-result">
            <b>✅ Uygulama tamamlandı.</b> {applied.summary.rows_applied} satır {applied.summary.materials} malzemeye yazıldı.
            Mevcut verified=true kayıtlar korundu.
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Btn variant="secondary" onClick={() => { reset(); onClose(); }}>Kapat</Btn>
        </div>
      </div>
    </Modal>
  );
}


function ImportHistoryModal({ open, onClose }) {
  const [batches, setBatches] = useState([]);
  useEffect(() => { if (open) api.get("/inventory/import/history").then(setBatches); }, [open]);
  return (
    <Modal open={open} onClose={onClose} title="Excel Import Geçmişi" size="xl">
      {batches.length === 0 ? (
        <div className="text-center text-slate-400 py-6 text-sm">Henüz Excel import kaydı yok.</div>
      ) : (
        <table className="w-full text-xs" data-testid="imp-history-table">
          <thead className="bg-slate-100"><tr>
            <th className="text-left px-2 py-1.5">Batch</th>
            <th className="text-left px-2 py-1.5">Dosya</th>
            <th className="text-left px-2 py-1.5">Yükleyen</th>
            <th className="text-left px-2 py-1.5">Tarih</th>
            <th className="text-right px-2 py-1.5">Toplam</th>
            <th className="text-right px-2 py-1.5">Oluşturuldu</th>
            <th className="text-right px-2 py-1.5">Uyarılı</th>
            <th className="text-right px-2 py-1.5">Atlandı</th>
          </tr></thead>
          <tbody>
            {batches.map(b => (
              <tr key={b.batch_no} className="border-b hover:bg-slate-50" data-testid={`imp-history-${b.batch_no}`}>
                <td className="px-2 py-1 font-mono">{b.batch_no}</td>
                <td className="px-2 py-1">{b.file_name}</td>
                <td className="px-2 py-1">{b.uploaded_by_name}</td>
                <td className="px-2 py-1">{(b.uploaded_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-2 py-1 text-right font-mono">{b.total_rows}</td>
                <td className="px-2 py-1 text-right font-mono text-emerald-700">{b.created_count}</td>
                <td className="px-2 py-1 text-right font-mono text-amber-700">{b.with_warnings_count}</td>
                <td className="px-2 py-1 text-right font-mono text-slate-500">{b.skipped_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}

// Optional lot location column — safe for lots without location data.
// Never blocks rendering; onClick opens the LotLocationsDialog.
function LocationCell({ lot, onClick }) {
  const locs = Array.isArray(lot?.locations) ? lot.locations.filter(x => x && x.location_code) : [];
  if (locs.length === 0) {
    return (
      <button onClick={onClick} className="text-xs text-slate-400 hover:text-indigo-700 hover:underline"
              data-testid={`loc-cell-empty-${lot?.id}`}>—</button>
    );
  }
  if (locs.length === 1) {
    return (
      <button onClick={onClick} className="text-xs font-mono text-slate-700 hover:text-indigo-700 hover:underline"
              data-testid={`loc-cell-${lot?.id}`}>{locs[0].location_code}</button>
    );
  }
  return (
    <button onClick={onClick} className="text-xs font-mono text-indigo-700 hover:underline"
            data-testid={`loc-cell-${lot?.id}`} title={locs.map(l => l.location_code).join(", ")}>
      {locs.length} lokasyon
    </button>
  );
}



export function InventoryPage() {
  const [tab, setTab] = useState("stock");
  const [rawCardId, setRawCardId] = useState(null);
  const [stock, setStock] = useState([]);
  const [lots, setLots] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [open, setOpen] = useState(false);
  const [adjustLot, setAdjustLot] = useState(null);
  const [locLot, setLocLot] = useState(null);
  const [adjustDelta, setAdjustDelta] = useState(0);
  const [adjustNew, setAdjustNew] = useState("");
  const [adjustReason, setAdjustReason] = useState("");
  const [f, setF] = useState({ material_id: "", supplier: "", delivery_note: "", supplier_lot: "", mfg_date: "", expiry_date: "", qty_received: 0, qty_accepted: 0, unit_cost: 0, notes: "" });
  const [matOpen, setMatOpen] = useState(false);
  const [matEdit, setMatEdit] = useState(null);
  const [matF, setMatF] = useState(emptyMat);
  const [historyOpen, setHistoryOpen] = useState(null);
  const [filteredLots, setFilteredLots] = useState([]);
  // iter100: 3 sekmede paylaşılan ürün adı arama filtresi (FilterAndReportBar → parent)
  const [productSearch, setProductSearch] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importHistOpen, setImportHistOpen] = useState(false);
  const [bulkAllergenOpen, setBulkAllergenOpen] = useState(false); // iter192
  const [bulkDocOpen, setBulkDocOpen] = useState(false); // iter320
  const [onlyErrors, setOnlyErrors] = useState(false);
  // [iter73] Etiket Yazdır — envanterden reprint (msg-93 Bölüm B)
  const [labelPickMat, setLabelPickMat] = useState(null); // { material, lots }
  const [labelPreview, setLabelPreview] = useState(null); // { materialId, lotId, labelKind }
  // iter144 — Shelf-life warning modal (Satın Alma %50+ raf ömrü kullanılmışsa açık onay şart)
  const [shelfLifeModal, setShelfLifeModal] = useState(null); // { message, shelf_life, pendingPayload }
  const [shelfLifeAlerts, setShelfLifeAlerts] = useState({ alerts: [], count: 0, loaded: false });
  // [iter73 msg 98 madde 68-71] Material Edit modalında lot düzenleme
  const [matLots, setMatLots] = useState([]);
  const [lotEditDraft, setLotEditDraft] = useState({}); // { [lotId]: { lot_code?, mfg_date?, expiry_date? } }
  // [iter86] Kamera + Galeri + OCR — lot bazlı fotoğraf ile alan otomatik doldurma
  const [ocrLotId, setOcrLotId] = useState(null);   // busy sinyali (spinner)
  const [cameraLotId, setCameraLotId] = useState(null); // hangi lot için kamera açık
  // iter250 — Hammadde / Ambalaj kısayol filtresi. Kural: unit==="kg" → Hammadde,
  // unit==="adet" → Ambalaj. 3 sekmede (Stok Özeti · Malzemeler · Lotlar) uygulanır.
  const [mainKind, setMainKind] = useState("all"); // "all" | "raw" | "packaging"

  const load = useCallback(() => Promise.all([
    api.get("/stock-summary"), api.get("/lots"), api.get("/materials"),
    api.get("/inventory/shelf-life-alerts").catch(() => ({ alerts: [], count: 0 })),
  ]).then(([s, l, m, sl]) => {
    setStock(s); setLots(l); setMaterials(m);
    setShelfLifeAlerts({ alerts: sl?.alerts || [], count: sl?.count || 0, loaded: true });
  }), []);
  // iter266 — Rezerve detay modalı state
  const [reserveModal, setReserveModal] = useState(null); // {material, groups, totals}
  const [reserveBusy, setReserveBusy] = useState(false);
  const openReserveModal = useCallback(async (row) => {
    const mid = row.material_id || row.id;
    if (!mid) return;
    setReserveBusy(true);
    setReserveModal({ material: { name: row.name, code: row.code, unit: row.unit }, groups: [], totals: null, loading: true });
    try {
      const d = await api.get(`/stock-summary/${mid}/reservations`);
      setReserveModal({ ...d, loading: false });
    } catch (e) {
      setReserveModal({ material: { name: row.name }, groups: [], totals: null, error: e?.response?.data?.detail || "Rezerve detayı alınamadı" });
    } finally { setReserveBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // iter294k — Anlik senkronizasyon: 5s polling emniyet agi.
  // useLiveEvents zaten inventory.lot.approved dinliyor ama SSE koptugu
  // durumlar (mobil, agir sekme) icin arka plan yenileme fallback.
  useEffect(() => {
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 5000);
    return () => clearInterval(iv);
  }, [load]);

  // Live push — refresh inventory whenever a lot/material/PO changes anywhere in the app.
  useLiveEvents(
    ["inventory.lot.created", "inventory.lot.approved", "inventory.lot.adjusted", "inventory.consumed",
     "purchase_order.approved", "purchase_order.cancelled"],
    () => load()
  );

  const save = async (ack = false) => {
    const payload = {
      ...f,
      qty_received: parseFloat(f.qty_received),
      qty_accepted: parseFloat(f.qty_accepted || f.qty_received),
      unit_cost: parseFloat(f.unit_cost),
      ...(ack ? { shelf_life_ack: true } : {}),
    };
    try {
      await api.post("/lots", payload);
      setOpen(false);
      setShelfLifeModal(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      // iter144 — raf ömrü %50+ gate; modal aç, kullanıcı onay verirse ack ile retry
      if (e?.response?.status === 409 && d && d.code === "SHELF_LIFE_ACK_REQUIRED") {
        setShelfLifeModal({ message: d.message, shelf_life: d.shelf_life, pendingPayload: payload });
        return;
      }
      alert(d?.message || d || e?.message || "Kayıt başarısız");
    }
  };

  const approveLot = async (id) => { await api.post(`/lots/${id}/approve`); load(); };
  const mname = id => materials.find(m => m.id === id)?.name || "—";

  const openMat = async (r) => {
    // iter334 — Mevcut Stok = fiziksel (available + reserved), Stok Özeti ile aynı kaynak
    let total = lots.filter(l => l.material_id === r.id).reduce((s, l) => s + (l.qty_available || 0) + (l.qty_reserved || 0), 0);
    try {
      const fresh = await api.get("/stock-summary");
      const row = (fresh || []).find(s => s.material_id === r.id);
      if (row) total = Number(row.physical || 0);
    } catch { /* lot toplamı kullanılır */ }
    setMatEdit({ ...r, _current_stock: total });
    setMatF({ ...emptyMat, ...r });
    // [iter73 msg 98 madde 68-71] Malzeme lotlarını çek — modal içinde düzenlenebilir listelenir.
    try {
      const matLots = await api.get(`/lots?material_id=${r.id}`);
      setMatLots(Array.isArray(matLots) ? matLots : []);
    } catch { setMatLots([]); }
    setLotEditDraft({});
    setMatOpen(true);
  };
  const newMat = () => { setMatEdit(null); setMatF(emptyMat); setMatOpen(true); setMatLots([]); setLotEditDraft({}); };

  // [iter73 msg 98 madde 74-77] Lot alan düzenleme (Lot No, ÜRT, SKT). Miktarlar değişmez.
  const saveLotFields = async (lot) => {
    const draft = lotEditDraft[lot.id] || {};
    if (Object.keys(draft).length === 0) return;
    try {
      await api.put(`/lots/${lot.id}/edit-fields`, draft);
      // reload material lots
      const fresh = await api.get(`/lots?material_id=${lot.material_id}`);
      setMatLots(Array.isArray(fresh) ? fresh : []);
      setLotEditDraft(prev => { const { [lot.id]: _, ...rest } = prev; return rest; });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Lot güncellenemedi"); }
  };

  // [iter86] OCR — tedarikçi etiketinden lot_code/mfg_date/expiry_date çeker.
  // Aynı endpoint (photos/ocr/supplier-label) System.jsx satın alma teslim akışında da kullanılır.
  const runLotOcr = async (lot, file) => {
    if (!file) return;
    setOcrLotId(lot.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("save", "1");
      fd.append("ref_type", "lot");
      fd.append("ref_id", lot.id);
      const token = localStorage.getItem("erp_token");
      const res = await fetch(`${API}/photos/ocr/supplier-label`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || `OCR başarısız: ${res.status}`);
        return;
      }
      const data = await res.json();
      const ocr = data.ocr || {};
      // OCR sonuçlarını draft'a yaz — kullanıcı manuel düzeltebilir + Kaydet'e basar
      const draft = { ...(lotEditDraft[lot.id] || {}) };
      if (ocr.lot_no) draft.lot_code = ocr.lot_no;
      if (ocr.mfg_date) draft.mfg_date = ocr.mfg_date;
      if (ocr.expiry_date) draft.expiry_date = ocr.expiry_date;
      setLotEditDraft({ ...lotEditDraft, [lot.id]: draft });
      // ekrana yansıtmak için input default'unu güncellemek şart — matLots'a yeni ref'ler ver
      // Iter94: photo_id da _ocr_hint içine yazılıyor (LabelPhotoStrip önizleme için)
      const newPhotoId = data.photo_id;
      setMatLots(prev => prev.map(l => l.id === lot.id ? {
        ...l,
        _ocr_hint: {
          lot_code: ocr.lot_no || null,
          mfg_date: ocr.mfg_date || null,
          expiry_date: ocr.expiry_date || null,
          confidence: ocr.confidence || {},
          photo_ids: [...((l._ocr_hint && l._ocr_hint.photo_ids) || []), ...(newPhotoId ? [newPhotoId] : [])],
        },
      } : l));
    } catch (e) {
      alert("Etiket okunamadı. Bilgileri elle girebilirsiniz. (" + e.message + ")");
    } finally { setOcrLotId(null); }
  };
  const saveMat = async () => {
    try {
      if (matEdit) {
        const { stock_adjust_to, stock_adjust_reason, _current_stock, validation_errors, has_critical_errors, ...matPayload } = matF;
        await api.put(`/materials/${matEdit.id}`, matPayload);
        if (stock_adjust_to !== undefined && stock_adjust_to !== "" && !isNaN(parseFloat(stock_adjust_to))) {
          const newStock = parseFloat(stock_adjust_to);
          const currentStock = Number(matEdit._current_stock || 0);
          if (Math.abs(newStock - currentStock) > 0.001) {
            try {
              await api.post(`/materials/${matEdit.id}/adjust-stock`, { new_stock: newStock, reason: stock_adjust_reason || "Manuel stok düzeltmesi" });
            } catch (e) { alert(`Malzeme güncellendi ama stok düzeltmesi başarısız: ${e.response?.data?.detail || "hata"}`); }
          }
        }
      } else {
        const created = await api.post("/materials", { ...matF });
        // iter152 — Backend artık initial_stock'u atomik olarak INITIAL_STOCK
        // lot'una çeviriyor. Frontend'de ikinci POST /lots çağrısına GEREK YOK.
      }
      setMatOpen(false); load();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const delMat = async (r) => { if (window.confirm(`${r.name} silinsin mi?`)) { try { await api.del(`/materials/${r.id}`); load(); } catch (e) { alert(e.response?.data?.detail); } } };

  // [iter72] Envanterden etiket reprint (msg-93 Bölüm B madde 15-18)
  const openMatLabel = async (mat) => {
    const isKg = (mat.unit || "").toLowerCase() !== "adet" && (mat.unit || "").toLowerCase() !== "pcs";
    if (!isKg) {
      // ADET → direkt preview
      setLabelPreview({ materialId: mat.id, lotId: null, labelKind: "ADET" });
      return;
    }
    // KG → lot listesi getir, tek lot varsa direkt aç, çoklu ise seçim
    try {
      const lots = await api.get(`/material-labels/${mat.id}/lots`);
      if (!lots || lots.length === 0) {
        // [iter86] Dead-end YOK — kullanıcı yine de "Belirlenemedi" ile etiket basabilir
        setLabelPickMat({ material: mat, lots: [] });
        return;
      }
      if (lots.length === 1) {
        setLabelPreview({ materialId: mat.id, lotId: lots[0].id, labelKind: "KG" });
      } else {
        setLabelPickMat({ material: mat, lots });
      }
    } catch (e) { alert(e.response?.data?.detail || "Lot yüklenemedi"); }
  };

  const openHistory = async (matRow) => {
    try {
      const res = await api.get(`/materials/${matRow.id || matRow.material_id}/full-history`);
      setHistoryOpen({ material: matRow, ...res });
    } catch (e) { alert(e.response?.data?.detail || "Geçmiş yüklenemedi"); }
  };
  const downloadHistoryPDF = async (matRow) => {
    try {
      const blob = await api.getBlob(`/materials/${matRow.id || matRow.material_id}/full-history/pdf`);
      openPdfPreview(blob, `Hammadde_Gecmisi_${matRow.code || matRow.name || ""}.pdf`);
    } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
  };

  // iter276 — Malzeme hareket zaman çizgisi modalı. Son 10 hareket (giriş/rezerve/tüketim).
  const [movementsModal, setMovementsModal] = useState(null); // { material, timeline }
  const openMovements = async (matRow) => {
    try {
      const res = await api.get(`/materials/${matRow.id || matRow.material_id}/movements?limit=10`);
      setMovementsModal({ material: { ...matRow, ...(res.material || {}) }, timeline: res.timeline || [] });
    } catch (e) { alert(e.response?.data?.detail || "Hareketler yüklenemedi"); }
  };

  const enrichedStock = stock.map(s => {
    const available = Number(s.available || 0);
    const reserved = Number(s.reserved || 0);
    const phantom = Number(s.reserved_phantom || 0);
    // iter275 — Mevcut Stok yalnız FİZİKSEL stok olsun; phantom (hayali) rezerveler
    // eklenmez. Aksi durumda 0 fiziksel stoklu bir malzeme SO rezervasyonu geldiğinde
    // "mevcut" görünüyor ve yanıltıyordu. Kalan = mevcut - gerçek (lot-bağlı) rezerve.
    const physical = Number(s.physical || 0);           // qty_available + qty_reserved (lot-backed)
    const realReserved = Math.max(0, reserved - phantom);
    const mevcut = physical;
    const kalan = Math.max(0, physical - realReserved);
    const shortage = phantom > 0 ? phantom : 0;         // hayali rezerve = eksik fiziksel stok
    // enrich with validation_errors from materials for ⚠ column
    const mat = materials.find(m => m.id === (s.id || s.material_id)) || {};
    return { ...s, mevcut, rezerve: reserved, kalan, shortage,
             validation_errors: mat.validation_errors || [], has_critical_errors: !!mat.has_critical_errors,
             excel_row_number: mat.excel_row_number, import_batch_id: mat.import_batch_id };
  });

  const displayedMaterials = useMemo(() => {
    // iter100: Türkçe safe casefold + product-name arama filtresi (3 sekme paylaşımlı)
    const trLower = (s) => (s || "").toLocaleLowerCase("tr");
    const q = trLower(productSearch.trim());
    let arr = materials;
    if (onlyErrors) arr = arr.filter(m => (m.validation_errors || []).length > 0);
    if (q) arr = arr.filter(m => trLower(m.name).startsWith(q) || trLower(m.name).includes(q));
    if (mainKind === "raw") arr = arr.filter(m => (m.unit || "").toLowerCase() === "kg");
    else if (mainKind === "packaging") arr = arr.filter(m => (m.unit || "").toLowerCase() === "adet");
    return arr;
  }, [materials, onlyErrors, productSearch, mainKind]);
  const filteredStock = useMemo(() => {
    // iter100: aynı arama Stok Özeti sekmesine de uygulanır
    const trLower = (s) => (s || "").toLocaleLowerCase("tr");
    const q = trLower(productSearch.trim());
    let arr = enrichedStock;
    if (q) arr = arr.filter(s => trLower(s.name).startsWith(q) || trLower(s.name).includes(q));
    if (mainKind === "raw") arr = arr.filter(s => (s.unit || "").toLowerCase() === "kg");
    else if (mainKind === "packaging") arr = arr.filter(s => (s.unit || "").toLowerCase() === "adet");
    return arr;
  }, [enrichedStock, productSearch, mainKind]);
  // Lot rows do not carry `unit` directly; resolve from parent material.
  const lotUnitFor = useCallback((mid) => {
    const m = materials.find(x => x.id === mid);
    return (m?.unit || "").toLowerCase();
  }, [materials]);
  const kindCount = useMemo(() => {
    const raw = materials.filter(m => (m.unit || "").toLowerCase() === "kg").length;
    const pack = materials.filter(m => (m.unit || "").toLowerCase() === "adet").length;
    return { raw, pack };
  }, [materials]);
  const errorCount = materials.filter(m => (m.validation_errors || []).length > 0).length;

  const pgStock = usePagination(filteredStock, "inventory-stock", 100);
  const pgMaterials = usePagination(displayedMaterials, "inventory-materials", 100);
  const displayedLots = useMemo(() => {
    const trLower = (s) => (s || "").toLocaleLowerCase("tr");
    const q = trLower(productSearch.trim());
    let base;
    if (q) base = (filteredLots || []).filter(r => trLower(mname(r.material_id)).includes(q));
    else base = filteredLots.length > 0 || lots.length === 0 ? filteredLots : lots;
    if (mainKind === "raw")       base = base.filter(r => lotUnitFor(r.material_id) === "kg");
    if (mainKind === "packaging") base = base.filter(r => lotUnitFor(r.material_id) === "adet");
    return base;
  }, [filteredLots, lots, productSearch, mainKind, mname, lotUnitFor]);
  const pgLots = usePagination(displayedLots, "inventory-lots", 100);

  return (
    <div>
      <PageHeader title="Envanter Yönetimi" description="Stok, lotlar, malzeme tanımları ve alım işlemleri"
        actions={<div className="flex gap-2 flex-wrap">
          {/* iter293b — Build tag (kullanıcı bir yenilenmenin bundle'a düştüğünü görsel olarak
              doğrulayabilsin). Deploy sonrası kullanıcı sayfayı açtığında bu tag'i görmüyorsa
              cache eski demektir — hard-refresh (Ctrl+Shift+R) gerekir. */}
          <span className="text-[9px] font-mono uppercase bg-emerald-100 text-emerald-800 px-2 py-1 rounded border border-emerald-300 self-center"
                title="Bundle sürüm etiketi — kullanıcının en güncel yayını yüklediğini doğrular"
                data-testid="build-tag">v293b</span>
          <Btn variant="secondary" onClick={async () => await api.download("/inventory/import/template", "Envanter_Import_Sablonu.xlsx")} data-testid="dl-template"><Download size={14}/> Excel Şablonu İndir</Btn>
          <Btn variant="secondary" onClick={() => setImportOpen(true)} data-testid="open-import"><Upload size={14}/> Excel'den İçeri Aktar</Btn>
          <Btn variant="ghost" onClick={() => setImportHistOpen(true)} data-testid="open-import-history"><History size={14}/> Import Geçmişi</Btn>
          <Btn variant="ghost" onClick={() => setBulkAllergenOpen(true)} data-testid="open-bulk-allergen"><FileSpreadsheet size={14}/> Toplu Alerjen İçe Aktar</Btn>
          <Btn variant="secondary" onClick={() => setBulkDocOpen(true)} data-testid="open-bulk-doc"><FileText size={14}/> Toplu Belge Yükle</Btn>
          {/* Iter 202 §3 — Controlled stock list PDFs (preview-first, list-level). Icon-only. */}
          <div className="inline-flex items-center gap-1 border rounded-md px-1.5 py-1 bg-slate-50" title="Kontrollü Liste PDF'leri">
            <span className="text-[10px] font-semibold text-slate-600 mr-1">Liste PDF:</span>
            <button title="Hammadde Stok Listesi PDF" data-testid="inv-rm-stock-pdf"
              onClick={async () => { try { const blob = await api.getBlob("/inventory/rm-stock-list/pdf"); openPdfPreview(blob, "Hammadde_Stok_Listesi.pdf"); } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); } }}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] font-bold">HS</button>
            <button title="Kritik Stok Listesi PDF" data-testid="inv-critical-stock-pdf"
              onClick={async () => { try { const blob = await api.getBlob("/inventory/critical-stock-list/pdf"); openPdfPreview(blob, "Kritik_Stok_Listesi.pdf"); } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); } }}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-bold">KS</button>
            <button title="Lot / SKT Takip Listesi PDF" data-testid="inv-lot-skt-pdf"
              onClick={async () => { try { const blob = await api.getBlob("/inventory/lot-skt-list/pdf"); openPdfPreview(blob, "Lot_SKT_Listesi.pdf"); } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); } }}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-bold">SKT</button>
            <button title="Stok Hareket Raporu PDF" data-testid="inv-stock-movement-pdf"
              onClick={async () => { try { const blob = await api.getBlob("/inventory/stock-movement/pdf"); openPdfPreview(blob, "Stok_Hareket_Raporu.pdf"); } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); } }}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800"><FileText size={14}/></button>
            <button title="Satın Alma İhtiyaç Listesi PDF" data-testid="inv-requirement-list-pdf"
              onClick={async () => { try { const blob = await api.getBlob("/purchasing/requirement-list/pdf"); openPdfPreview(blob, "Ihtiyac_Listesi.pdf"); } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); } }}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800 text-[10px] font-bold">İL</button>
            <button title="Gelen Malzeme Listesi PDF" data-testid="inv-incoming-materials-pdf"
              onClick={async () => { try { const blob = await api.getBlob("/purchasing/incoming-materials/pdf"); openPdfPreview(blob, "Gelen_Malzeme_Listesi.pdf"); } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); } }}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800 text-[10px] font-bold">GM</button>
            <button title="Raf Ömrü %75 — Kritik Lotlar PDF" data-testid="inv-shelf-life-pdf"
              onClick={async () => { try { const blob = await api.getBlob("/inventory/shelf-life-alerts/pdf"); openPdfPreview(blob, "Raf_Omru_Kritik_Lotlar.pdf"); } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); } }}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-rose-100 hover:bg-rose-200 border border-rose-300 text-rose-800 text-[10px] font-bold">RÖ</button>
          </div>
          <Btn variant="secondary" onClick={newMat} data-testid="new-material"><Plus size={14}/> Yeni Malzeme</Btn>
          <Btn onClick={() => setOpen(true)} data-testid="new-lot"><PackagePlus size={14}/> Mal Kabul</Btn>
        </div>} />
      <FilterAndReportBar
        reportType="inventory" rows={lots}
        statuses={[{value:"approved",label:"Onaylı"},{value:"awaiting_qc",label:"QC Bekliyor"},{value:"blocked",label:"Bloklu"},{value:"rejected",label:"Red"}]}
        dateAccessor={r => r.created_at || r.mfg_date}
        productAccessor={r => mname(r.material_id)}
        statusAccessor={r => r.status}
        onFiltered={setFilteredLots}
        onProductQuery={setProductSearch}
        unit={mainKind === "raw" ? "kg" : mainKind === "packaging" ? "adet" : ""}
        showExcel
      />
      <div className="mb-4 flex gap-2 flex-wrap items-center">
        {[{ k: "stock", l: "Stok Özeti", c: "teal" }, { k: "materials", l: "Malzemeler", c: "indigo" }, { k: "lots", l: "Lotlar", c: "amber" }, { k: "cards", l: "Malzeme Kartları", c: "indigo" }].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`inv-tab-${t.k}`}
            className={`px-4 py-2 text-sm rounded-lg border transition-all ${tab === t.k ? `bg-${t.c}-100 border-${t.c}-300 text-${t.c}-800 font-semibold shadow-sm` : `bg-white border-slate-200 text-slate-600 hover:bg-${t.c}-50 hover:border-${t.c}-200`}`}>
            {t.l}
          </button>
        ))}
        {tab !== "cards" && (
          <div className="inline-flex items-center gap-1 ml-2 border border-slate-200 rounded-full p-0.5 bg-white"
               data-testid="inv-kind-filter" title="Birim filtresi — kg (Hammadde) / adet (Ambalaj) / Tümü">
            <span className="text-[10px] font-semibold text-slate-500 uppercase px-2">Birim</span>
            {[
              { k: "all",       l: "Tümü",     c: "slate" },
              { k: "raw",       l: `kg${kindCount.raw ? " · " + kindCount.raw : ""}`,     c: "teal" },
              { k: "packaging", l: `adet${kindCount.pack ? " · " + kindCount.pack : ""}`, c: "amber" },
            ].map(o => (
              <button key={o.k} type="button" onClick={() => setMainKind(o.k)}
                data-testid={`inv-kind-${o.k}`}
                className={`px-3 py-1 text-xs rounded-full transition-all ${mainKind === o.k ? `bg-${o.c}-100 text-${o.c}-800 font-semibold` : "text-slate-600 hover:bg-slate-100"}`}>
                {o.l}
              </button>
            ))}
          </div>
        )}
        {tab === "materials" && errorCount > 0 && (
          <button
            onClick={() => setOnlyErrors(o => !o)}
            className={`ml-auto px-3 py-1.5 text-xs rounded-full border transition-all inline-flex items-center gap-1 ${onlyErrors ? "bg-red-100 border-red-300 text-red-800 font-semibold" : "bg-white border-slate-200 text-slate-600 hover:bg-red-50 hover:border-red-200"}`}
            data-testid="filter-only-errors"
          >
            <Filter size={12}/> Düzeltme Gerekenler ({errorCount})
          </button>
        )}
      </div>

      {tab === "stock" && (
        <>
        <Table testid="stock-table" columns={[
          { label: "", render: r => (r.validation_errors && r.validation_errors.length > 0) ? <ErrorTooltip material={{ ...r, id: r.id || r.material_id || `stock-${r.code}` }}/> : null },
          { label: "Kod", render: r => <span className="font-mono text-xs">{r.code}</span> },
          { label: "Malzeme", render: r => <button onClick={() => openHistory(r)} className="text-indigo-700 hover:underline text-left" data-testid={`mat-history-${r.id || r.material_id}`}>{r.name}</button> },
          { label: "Kategori", render: r => CATEGORY_LABEL[r.category] || r.category },
          { label: "Mevcut Stok", render: r => {
              // iter275 — Fiziki mevcut + eksik uyarısı + Sipariş Oluştur butonu.
              // iter276 — Hareket zaman çizgisi ikonu (⏱) — tüm malzemeler için.
              const short = Number(r.shortage || 0);
              return (
                <div className="inline-flex items-center gap-1 flex-wrap">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); openMovements(r); }}
                    className="p-0.5 rounded hover:bg-indigo-100 text-slate-500 hover:text-indigo-700"
                    title="Son 10 hareketi göster"
                    data-testid={`stock-movements-${r.material_id || r.id}`}
                  ><Clock size={13}/></button>
                  <span className="font-mono">{r.mevcut.toFixed(2)} {r.unit}</span>
                  {short > 0 && (
                    <>
                      <span
                        className="text-[9px] font-bold uppercase px-1 py-0.5 rounded border bg-rose-50 border-rose-300 text-rose-700"
                        title={`Rezerve talep fiziki stoktan ${short.toFixed(2)} ${r.unit} fazla — satın alma gerekli`}
                        data-testid={`stock-shortage-${r.material_id || r.id}`}
                      >⚠ {short.toFixed(2)} eksik</span>
                      <Link
                        to={`/purchasing?tab=needs&mat=${r.material_id || r.id}`}
                        className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border bg-indigo-600 border-indigo-700 text-white hover:bg-indigo-700"
                        title="Bu eksik için sipariş oluştur"
                        data-testid={`stock-order-btn-${r.material_id || r.id}`}
                        onClick={(e) => e.stopPropagation()}
                      >Sipariş Oluştur</Link>
                    </>
                  )}
                </div>
              );
          } },
          { label: "Rezerve", render: r => {
              const total = Number(r.rezerve || 0);
              const phantom = Number(r.reserved_phantom || 0);
              const real = Math.max(0, total - phantom);
              if (total <= 0) return <span className="font-mono text-slate-400">0.00</span>;
              return (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); openReserveModal(r); }}
                  data-testid={`reserve-open-${r.material_id || r.id}`}
                  className="inline-flex items-center gap-1 font-mono text-amber-700 hover:text-amber-900 hover:underline"
                  title="Rezerve detayını görüntüle"
                >
                  <span>{total.toFixed(2)}</span>
                  {phantom > 0 && (
                    <span
                      className="text-[9px] font-bold uppercase px-1 py-0.5 rounded border bg-rose-50 border-rose-300 text-rose-700"
                      title={`Fiziki stokta karşılığı olmayan hayali rezerve: ${phantom.toFixed(2)}${real > 0 ? ` · Gerçek: ${real.toFixed(2)}` : ""}`}
                      data-testid={`reserve-phantom-badge-${r.material_id || r.id}`}
                    >
                      ⚠ {phantom.toFixed(2)}
                    </span>
                  )}
                </button>
              );
          } },
          { label: "Kalan", render: r => <span className={`font-mono font-semibold ${r.kalan <= 0 ? "text-red-600" : "text-emerald-700"}`}>{r.kalan.toFixed(2)}</span> },
          { label: "Karantina", render: r => Number(r.quarantine || 0).toFixed(2) },
          { label: "Kritik", render: r => r.critical_stock },
          { label: "Durum", render: r => <Badge level={r.level}>{r.level === "ok" ? "Yeterli" : r.level === "attention" ? "Dikkat" : r.level === "critical" ? "Kritik" : "Eksik"}</Badge> },
        ]} rows={pgStock.paged} />
        <ListPagination pagination={pgStock} testid="stock-pagination"/>
        </>
      )}

      {tab === "materials" && (
        <>
        <Table testid="materials-table" columns={[
          { label: "", render: r => (r.validation_errors && r.validation_errors.length > 0) ? <ErrorTooltip material={r}/> : null },
          { label: "Kod", render: r => <span className="font-mono text-xs">{r.code}</span> },
          { label: "Malzeme", render: r => <button onClick={() => openHistory(r)} className="text-indigo-700 hover:underline text-left">{r.name}</button> },
          { label: "Kategori", render: r => CATEGORY_LABEL[r.category] || r.category || <span className="text-slate-400">—</span> },
          { label: "Birim", key: "unit" },
          { label: "Kritik", key: "critical_stock" },
          { label: "Tedarik Süresi", render: r => `${r.lead_time_days || 0} gün` },
          { label: "Kaynak", render: r => r.import_source === "EXCEL_IMPORT" ? <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded" title={`Batch: ${r.import_batch_id} · Satır: ${r.excel_row_number}`}>Excel</span> : <span className="text-slate-400 text-[10px]">Manuel</span> },
          { label: "Durum", render: r => <Badge level={r.active ? "ok" : "inactive"}>{r.active ? "Aktif" : "Pasif"}</Badge> },
          { label: "İşlem", render: r => (
            <div className="flex gap-1" onClick={e => e.stopPropagation()}>
              {/* iter330 — İkon-only aksiyonlar + native title tooltip (1 sn hover). */}
              <Btn size="sm" variant="secondary" onClick={() => openMat(r)} data-testid={`edit-mat-${r.id}`} title="Düzenle"><Pencil size={12}/></Btn>
              <Btn size="sm" variant="danger" onClick={() => delMat(r)} data-testid={`delete-mat-${r.id}`} title="Sil"><Trash2 size={12}/></Btn>
              <Btn size="sm" variant="secondary" onClick={() => openMatLabel(r)} data-testid={`label-mat-${r.id}`} title="Etiket Yazdır"><Tag size={12}/></Btn>
              {(r.kind === "raw_material" || r.kind === "raw"
                || r.main_kind === "raw"
                || r.category === "raw" || r.category === "raw_material") && (
                <Btn size="sm" variant="secondary" onClick={() => setRawCardId(r.id)}
                     data-testid={`raw-card-${r.id}`} title="Hammadde Teknik & Mevzuat Kartı">
                  <Bookmark size={12}/>
                </Btn>
              )}
              {(r.kind === "packaging" || r.main_kind === "packaging" || (r.category && String(r.category).toLowerCase().includes("ambalaj"))) && (
                <Btn size="sm" variant="secondary" onClick={async () => {
                  try {
                    const blob = await api.getBlob(`/inventory/packaging-card/${r.id}/pdf`);
                    openPdfPreview(blob, `Ambalaj_Karti_${r.code || r.id.slice(0, 6)}.pdf`);
                  } catch (e) { alert(e.response?.data?.detail || "PDF alınamadı"); }
                }} data-testid={`packaging-card-pdf-${r.id}`} title="Ambalaj Malzemesi Kartı Kontrollü PDF">
                  <FileText size={12}/>
                </Btn>
              )}
            </div>
          ) },
        ]} rows={pgMaterials.paged} />
        <ListPagination pagination={pgMaterials} testid="materials-pagination"/>
        </>
      )}

      {tab === "lots" && (
        <>
          {/* iter144 — Envanter %75 raf ömrü uyarı özet kartı (SKT calendar alerts'e ek erken uyarı) */}
          {shelfLifeAlerts.loaded && shelfLifeAlerts.count > 0 && (
            <Card className="p-3 mb-3 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300 dark:from-amber-900/20 dark:to-orange-900/20 dark:border-amber-500/40" data-testid="shelf-life-alerts-card">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={16} className="text-amber-700"/>
                <span className="text-sm font-semibold text-amber-900 dark:text-amber-200">Raf Ömrü %75+ Tamamlanan Lotlar ({shelfLifeAlerts.count})</span>
              </div>
              <div className="space-y-1 overflow-y-auto text-xs" style={{ maxHeight: "260px" }}>
                {shelfLifeAlerts.alerts.map(a => (
                  <div key={a.lot_id} className="flex items-center gap-2 p-1.5 rounded bg-white/60 dark:bg-slate-800/60 border border-amber-200 dark:border-amber-500/30" data-testid={`shelf-life-alert-${a.lot_id}`}>
                    <span className="font-mono text-[10px] bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-300 px-1.5 py-0.5 rounded">%{a.used_pct}</span>
                    <span className="font-semibold flex-1 truncate">{a.material_name}</span>
                    <span className="text-slate-500 font-mono">{a.lot_code}</span>
                    <span className="text-slate-500">SKT {(a.expiry_date || "").slice(0, 10)} · Kalan {a.remaining_days}g · Stok {a.qty_available} {a.unit}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        <Table columns={[
          { label: "Lot / Ted. Lot", render: r => <span className="font-mono text-xs">{r.lot_code}</span> },
          { label: "Malzeme", render: r => mname(r.material_id) },
          { label: "SKT", render: r => {
              const alert = shelfLifeAlerts.alerts.find(a => a.lot_id === r.id);
              return (
                <div className="flex items-center gap-1">
                  <span>{(r.expiry_date || "").slice(0, 10) || "—"}</span>
                  {alert && (
                    <span
                      className="text-[9px] font-bold uppercase px-1 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-300"
                      title={`Raf ömrünün %${alert.used_pct}'i tamamlandı — kalan %${alert.remaining_pct} (${alert.remaining_days} gün)`}
                      data-testid={`lot-shelf-life-badge-${r.id}`}
                    >
                      %{alert.used_pct}
                    </span>
                  )}
                </div>
              );
          } },
          { label: "Mevcut", render: r => {
              const v = Number(r.qty_available || 0);
              if (v < 0) { try { console.warn("[stock-integrity] negative qty_available in lot", r.id, v); } catch(_){} }
              return <span className={v < 0 ? "text-rose-600 font-semibold" : ""}>{Math.max(0, v)}</span>;
          } },
          { label: "Rezerve", render: r => {
              const v = Number(r.qty_reserved || 0);
              if (v < 0) { try { console.warn("[stock-integrity] negative qty_reserved in lot", r.id, v); } catch(_){} }
              return <span className={v < 0 ? "text-rose-600 font-semibold" : ""}>{Math.max(0, v)}</span>;
          } },
          { label: "Lokasyon", render: r => <LocationCell lot={r} onClick={() => setLocLot(r)} /> },
          { label: "Durum", render: r => <Badge level={r.status === "approved" ? "ok" : r.status === "awaiting_qc" ? "attention" : r.status === "blocked" || r.status === "rejected" ? "missing" : "inactive"}>{r.status}</Badge> },
          { label: "İşlem", render: r => (
            <div className="flex gap-1" onClick={e => e.stopPropagation()}>
              {r.status === "awaiting_qc" && <Btn size="sm" variant="secondary" onClick={() => approveLot(r.id)} data-testid={`approve-lot-${r.id}`} title="Onayla"><ShieldCheck size={12}/></Btn>}
              <Btn size="sm" variant="secondary" onClick={() => setAdjustLot(r)} data-testid={`adjust-lot-${r.id}`} title="Miktar Düzenle"><ArrowUpCircle size={12}/></Btn>
              <Btn size="sm" variant="secondary" onClick={() => setLocLot(r)} data-testid={`loc-lot-${r.id}`} title="Lokasyon"><Bookmark size={12}/></Btn>
              <Btn size="sm" variant="danger" onClick={async () => { if (window.confirm("Lot silinsin/arşivlensin mi?")) { try { await api.del(`/lots/${r.id}`); load(); } catch (e) { alert(e.response?.data?.detail); } } }} data-testid={`delete-lot-${r.id}`} title="Lotu Sil / Arşivle"><X size={12}/></Btn>
            </div>
          ) },
        ]} rows={pgLots.paged} />
        <ListPagination pagination={pgLots} testid="lots-pagination"/>
        </>
      )}

      {tab === "cards" && (
        <MaterialsHubPage embedded />
      )}

      <ImportWizard open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />
      <ImportHistoryModal open={importHistOpen} onClose={() => setImportHistOpen(false)} />
      <BulkAllergenImportModal open={bulkAllergenOpen} onClose={() => setBulkAllergenOpen(false)} />
      <BulkDocumentUploadModal open={bulkDocOpen} onClose={() => setBulkDocOpen(false)} onCommitted={load} />

      <Modal open={!!historyOpen} onClose={() => setHistoryOpen(null)} title={`Hammadde Geçmişi — ${historyOpen?.material?.name || ""}`} size="xl">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <History size={12}/> Son 24 ay içindeki tüm hareketler · <b>{historyOpen?.count || 0}</b> kayıt
          </div>
          <Btn size="sm" variant="secondary" onClick={() => downloadHistoryPDF(historyOpen?.material)} data-testid="mat-history-pdf">
            <FileText size={12}/> PDF Oluştur
          </Btn>
        </div>
        {(historyOpen?.timeline || []).length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-sm">Son 2 yılda hareket bulunamadı.</div>
        ) : (
          <div className="overflow-y-auto border rounded" style={{ maxHeight: "480px" }} data-testid="mat-history-list">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b sticky top-0"><tr>
                {["Tarih", "Hareket", "Lot", "Miktar", "Yön", "İlişkili", "Kullanıcı", "Açıklama"].map(h => <th key={h} className="text-left px-2 py-1.5 font-semibold">{h}</th>)}
              </tr></thead>
              <tbody>
                {(historyOpen?.timeline || []).map((ev, i) => {
                  const label = ev.kind_label || ({
                    receipt: "Mal Kabul", excel_import: "Excel Import", lot_create: "Lot Girişi",
                    reservation: "Rezervasyon", production_consumption: "Üretim Tüketimi",
                    initial_stock: "Başlangıç Stoğu", adjustment_up: "Stok Düzeltme (+)",
                    adjustment_down: "Stok Düzeltme (−)", adjustment: "Stok Düzeltme",
                    waste: "Fire", quarantine: "Karantina", qc_release: "QC Onay",
                  }[ev.kind] || ev.kind);
                  const dirColor = ev.direction === "in" ? "bg-emerald-100 text-emerald-800" : ev.direction === "out" ? "bg-rose-100 text-rose-800" : ev.direction === "reserve" ? "bg-indigo-100 text-indigo-800" : "bg-slate-100 text-slate-600";
                  const dirLabel = { in: "GİRİŞ", out: "ÇIKIŞ", reserve: "REZERVE" }[ev.direction] || "—";
                  const rel = [
                    ev.po_no && `PO ${ev.po_no}`,
                    ev.prod_order_no && `ÜE ${ev.prod_order_no}`,
                    ev.so_no && `SO ${ev.so_no}`,
                    ev.supplier && `Ted. ${ev.supplier}`,
                    ev.customer_name && `Müş. ${ev.customer_name}`,
                    ev.product_name && `Ürün ${ev.product_name}`,
                  ].filter(Boolean).join(" · ");
                  return (
                    <tr key={i} className="border-b hover:bg-slate-50" data-testid={`mat-history-row-${i}`}>
                      <td className="px-2 py-1.5 font-mono text-[11px]">{(ev.ts || "").slice(0, 10)}</td>
                      <td className="px-2 py-1.5"><span className="text-xs font-semibold text-slate-700">{label}</span></td>
                      <td className="px-2 py-1.5 font-mono text-[11px]">{ev.lot_code || ev.supplier_lot || "—"}</td>
                      <td className="px-2 py-1.5 font-mono">{(ev.qty || 0).toFixed(2)}</td>
                      <td className="px-2 py-1.5"><span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${dirColor}`}>{dirLabel}</span></td>
                      <td className="px-2 py-1.5 text-[11px] text-slate-600">{rel || "—"}</td>
                      <td className="px-2 py-1.5 text-[11px]">{ev.user_name || "—"}</td>
                      <td className="px-2 py-1.5 text-[11px] text-slate-500">{ev.notes || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal>

      <Modal open={matOpen} onClose={() => setMatOpen(false)} title={matEdit ? "Malzeme Düzenle" : "Yeni Malzeme"} size="lg">
        {matEdit && (matEdit.validation_errors || []).length > 0 && (
          <div className="mb-3 bg-amber-50 border border-amber-200 rounded p-2 text-xs" data-testid="mat-edit-errors-banner">
            <div className="font-semibold text-amber-800 mb-1">⚠ Bu malzeme kaydında düzeltme gereken alanlar var:</div>
            <ul className="ml-4 space-y-0.5 text-amber-900">
              {(matEdit.validation_errors || []).map((e, i) => (
                <li key={i}>• {e.message}</li>
              ))}
            </ul>
            {matEdit.excel_row_number && <div className="text-[10px] mt-1 text-amber-700 italic">Kaynak: Excel Satırı {matEdit.excel_row_number} · Batch {matEdit.import_batch_id}</div>}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Input label="Malzeme Adı *" value={matF.name} onChange={e => setMatF({ ...matF, name: e.target.value })} data-testid="mat-name" /></div>
          <Select label="Kategori *" value={matF.category} onChange={e => setMatF({ ...matF, category: e.target.value })} data-testid="mat-category">
            <option value="">— Seçiniz —</option>
            {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          <Select label="Birim *" value={matF.unit} onChange={e => setMatF({ ...matF, unit: e.target.value })} data-testid="mat-unit">
            <option value="">— Seçiniz —</option>
            <option value="kg">kg</option><option value="adet">adet</option>
          </Select>
          <Input label="Kritik Stok" type="number" value={matF.critical_stock} onChange={e => setMatF({ ...matF, critical_stock: parseFloat(e.target.value) })} />
          <Input label="Tedarik Süresi (gün)" type="number" value={matF.lead_time_days} onChange={e => setMatF({ ...matF, lead_time_days: parseInt(e.target.value) })} />
          <Input label="Birim Maliyet" type="number" step="0.01" value={matF.unit_cost} onChange={e => setMatF({ ...matF, unit_cost: parseFloat(e.target.value) })} />
          <Select label="Para Birimi" value={matF.currency} onChange={e => setMatF({ ...matF, currency: e.target.value })}>
            <option value="EUR">EUR</option><option value="USD">USD</option><option value="TRY">TRY</option>
          </Select>
          <Select label="Durum" value={matF.active ? "1" : "0"} onChange={e => setMatF({ ...matF, active: e.target.value === "1" })}>
            <option value="1">Aktif</option><option value="0">Pasif</option>
          </Select>
          {matEdit && (
            <>
              <div className="col-span-2 pt-2 mt-1 border-t border-slate-200"><div className="text-xs font-semibold uppercase text-amber-700 tracking-wider">Mevcut Stok Düzeltmesi <span className="text-slate-400 font-normal normal-case">(opsiyonel — stok sayımı sonrası düzeltme)</span></div></div>
              <div className="col-span-2 bg-slate-50 border border-slate-200 rounded p-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Sistemdeki Mevcut Stok:</span>
                  <span className="font-mono font-semibold text-slate-800">{Number(matEdit._current_stock || 0).toFixed(2)} {matF.unit}</span>
                </div>
                <div className="text-[10px] text-slate-500 italic mt-1">Aşağıya sayım sonucu bulunan gerçek Mevcut Stok değerini girin — girilen değer kesin (mutlak) değerdir; rezervasyonlar korunur.</div>
              </div>
              <Input label={`Sayım Sonucu (${matF.unit})`} type="number" step="0.01" value={matF.stock_adjust_to ?? ""} onChange={e => setMatF({ ...matF, stock_adjust_to: e.target.value })} data-testid="mat-stock-adjust" placeholder="Fiziksel sayım sonucu"/>
              <Input label="Düzeltme Notu" value={matF.stock_adjust_reason || ""} onChange={e => setMatF({ ...matF, stock_adjust_reason: e.target.value })} data-testid="mat-stock-reason" placeholder="Neden? (sayım, fire, hasar...)"/>
            </>
          )}
          {!matEdit && (
            <>
              <div className="col-span-2 pt-2 mt-1 border-t border-slate-200"><div className="text-xs font-semibold uppercase text-teal-700 tracking-wider">Başlangıç Stoğu <span className="text-slate-400 font-normal normal-case">(opsiyonel — girilirse otomatik onaylı lot oluşturulur)</span></div></div>
              <Input label={`Mevcut Stok (${matF.unit})`} type="number" step="0.01" value={matF.initial_stock} onChange={e => setMatF({ ...matF, initial_stock: parseFloat(e.target.value) || 0 })} data-testid="mat-initial-stock" />
              <Input label="Tedarikçi (opsiyonel)" value={matF.initial_supplier} onChange={e => setMatF({ ...matF, initial_supplier: e.target.value })} data-testid="mat-initial-supplier" placeholder="örn. Başlangıç Stoğu" />
            </>
          )}
          {/* [iter73 msg 98 madde 68-77] Malzeme Lotları — Lot No / ÜRT / SKT düzenlenebilir */}
          {matEdit && (
            <div className="col-span-2 pt-3 mt-2 border-t border-slate-200" data-testid="mat-lots-section">
              <div className="text-xs font-semibold uppercase text-indigo-700 tracking-wider mb-2 flex items-center gap-2">
                <History size={12}/> Malzeme Lotları
                <span className="text-[10px] font-normal text-slate-500 normal-case">({matLots.length} lot · miktar değişmez, sadece tanımlayıcı alanlar güncellenir)</span>
              </div>
              {matLots.length === 0 && <div className="text-[11px] text-slate-400 italic py-2">Bu malzeme için lot kaydı yok.</div>}
              <div className="max-h-64 overflow-y-auto space-y-1">
                {matLots.map(lot => {
                  const d = lotEditDraft[lot.id] || {};
                  const dirty = Object.keys(d).length > 0;
                  const hint = lot._ocr_hint || {};
                  const busy = ocrLotId === lot.id;
                  // OCR sonrası input'un DOM default'u eski değeri gösteriyor. Draft'ta value varsa
                  // controlled input olarak göster; yoksa uncontrolled defaultValue kalsın.
                  const val = (k) => d[k] !== undefined ? d[k] : (hint[k] !== undefined && hint[k] !== null ? hint[k] : (lot[k === "lot_code" ? "lot_code" : k] || "").slice ? (lot[k === "lot_code" ? "lot_code" : k] || "").slice(0, k === "lot_code" ? 100 : 10) : (lot[k] || ""));
                  const setDraft = (k, v) => setLotEditDraft({ ...lotEditDraft, [lot.id]: { ...d, [k]: v }});
                  return (
                    <div key={lot.id} className="grid grid-cols-12 gap-1 items-end bg-slate-50/60 border border-slate-200 rounded p-1.5" data-testid={`mat-lot-${lot.id}`}>
                      <div className="col-span-3">
                        <label className="text-[9px] uppercase text-slate-500">Lot No</label>
                        <input type="text" value={val("lot_code")}
                          onChange={e => setDraft("lot_code", e.target.value)}
                          className="w-full px-1.5 py-1 text-xs border border-slate-300 rounded font-mono"
                          data-testid={`lot-edit-code-${lot.id}`}/>
                      </div>
                      <div className="col-span-2">
                        <label className="text-[9px] uppercase text-slate-500">ÜRT</label>
                        <input type="date" value={val("mfg_date")}
                          onChange={e => setDraft("mfg_date", e.target.value)}
                          className="w-full px-1.5 py-1 text-xs border border-slate-300 rounded"
                          data-testid={`lot-edit-mfg-${lot.id}`}/>
                      </div>
                      <div className="col-span-2">
                        <label className="text-[9px] uppercase text-slate-500">SKT</label>
                        <input type="date" value={val("expiry_date")}
                          onChange={e => setDraft("expiry_date", e.target.value)}
                          className="w-full px-1.5 py-1 text-xs border border-slate-300 rounded"
                          data-testid={`lot-edit-exp-${lot.id}`}/>
                      </div>
                      {/* [iter86] Kamera + Galeri OCR butonları */}
                      <div className="col-span-3 flex items-center gap-1 pt-3">
                        <button
                          type="button"
                          onClick={() => setCameraLotId(lot.id)}
                          disabled={busy}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs border border-slate-200 bg-white hover:bg-sky-50 disabled:opacity-50"
                          title="Kamera ile Etiket Çek — OCR ile Lot/ÜRT/SKT alanlarını doldurur"
                          data-testid={`lot-ocr-camera-${lot.id}`}
                        >
                          {busy ? <Loader2 size={12} className="animate-spin"/> : <Camera size={12}/>}
                          <span className="hidden sm:inline">Kamera</span>
                        </button>
                        <label
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs border border-slate-200 bg-white hover:bg-emerald-50 cursor-pointer ${busy ? 'opacity-50 pointer-events-none' : ''}`}
                          title="Galeriden Fotoğraf Seç — OCR ile alanları doldurur"
                          data-testid={`lot-ocr-gallery-${lot.id}`}
                        >
                          <ImageIcon size={12}/>
                          <span className="hidden sm:inline">Galeri</span>
                          <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) runLotOcr(lot, f); e.target.value = ""; }}/>
                        </label>
                      </div>
                      <div className="col-span-2 flex flex-col gap-0.5">
                        <span className="text-[9px] text-slate-500">Mevcut: <b className="font-mono">{lot.qty_available}</b></span>
                        <Btn size="sm" variant={dirty ? "default" : "secondary"} onClick={() => saveLotFields(lot)}
                             disabled={!dirty} data-testid={`lot-save-${lot.id}`}>{dirty ? "Kaydet" : "—"}</Btn>
                      </div>
                      {(hint.photo_ids || []).length > 0 && (
                        <div className="col-span-12 -mt-0.5 pt-1 border-t border-slate-200 flex items-center gap-2">
                          <span className="text-[9px] uppercase text-slate-500">Etiket Foto</span>
                          <LabelPhotoStrip photoIds={hint.photo_ids} size={40} testid={`lot-label-photos-${lot.id}`}/>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="text-[10px] text-slate-400 italic mt-1">
                Miktar (qty_available/qty_reserved) bu ekrandan değişmez. Geçmiş üretim consumption snapshot'ları korunur — sadece güncel Inventory master lot bilgisi güncellenir.
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="secondary" onClick={() => setMatOpen(false)}>Vazgeç</Btn>
          <Btn onClick={saveMat} data-testid="save-material">Kaydet</Btn>
        </div>
      </Modal>

      {locLot && <LotLocationsDialog lot={locLot} onClose={() => setLocLot(null)} onSaved={() => load()} />}
      <Modal open={!!adjustLot} onClose={() => setAdjustLot(null)} title={`Miktar Düzenle — ${adjustLot?.lot_code || ""}`}>
        <div className="text-sm mb-2 text-slate-600">Mevcut: <span className="font-mono font-semibold">{adjustLot?.qty_available}</span></div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Değişim (+/-)" type="number" step="0.01" value={adjustDelta} onChange={e => setAdjustDelta(e.target.value)} data-testid="adjust-delta"/>
          <Input label="Yeni Miktar (opsiyonel)" type="number" step="0.01" value={adjustNew} onChange={e => { setAdjustNew(e.target.value); if (adjustLot) setAdjustDelta(parseFloat(e.target.value) - adjustLot.qty_available); }} />
          <div className="col-span-2"><Input label="Gerekçe *" value={adjustReason} onChange={e => setAdjustReason(e.target.value)} data-testid="adjust-reason"/></div>
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setAdjustLot(null)}>Vazgeç</Btn><Btn onClick={async () => { if (!adjustReason) { alert("Gerekçe zorunlu"); return; } try { await api.post(`/lots/${adjustLot.id}/adjust`, { delta: parseFloat(adjustDelta), reason: adjustReason }); setAdjustLot(null); setAdjustDelta(0); setAdjustNew(""); setAdjustReason(""); load(); } catch (e) { alert(e.response?.data?.detail); } }} data-testid="save-adjust">Uygula</Btn></div>
      </Modal>

      <Modal open={open} onClose={() => setOpen(false)} title="Mal Kabul (Lot Girişi)" size="lg">
        <div className="grid grid-cols-2 gap-3">
          <Select label="Malzeme *" value={f.material_id} onChange={e => setF({ ...f, material_id: e.target.value })} data-testid="lot-material">
            <option value="">Seçiniz...</option>{materials.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
          </Select>
          <Input label="Tedarikçi" value={f.supplier} onChange={e => setF({ ...f, supplier: e.target.value })} />
          <Input label="İrsaliye No" value={f.delivery_note} onChange={e => setF({ ...f, delivery_note: e.target.value })} />
          <Input label="Lot / Tedarikçi Lot" value={f.supplier_lot} onChange={e => setF({ ...f, supplier_lot: e.target.value })} data-testid="lot-supplier-lot"/>
          <Input label="Miktar *" type="number" step="0.01" value={f.qty_received} onChange={e => setF({ ...f, qty_received: e.target.value })} data-testid="lot-qty"/>
          <Input label="Üretim Tarihi" type="date" value={(f.mfg_date || "").slice(0,10)} onChange={e => setF({ ...f, mfg_date: e.target.value })} />
          <Input label="SKT" type="date" value={(f.expiry_date || "").slice(0,10)} onChange={e => setF({ ...f, expiry_date: e.target.value })} />
          <Input label="Birim Maliyet" type="number" step="0.01" value={f.unit_cost} onChange={e => setF({ ...f, unit_cost: e.target.value })} />
          <div className="col-span-2"><Textarea label="Notlar" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn><Btn onClick={() => save(false)} data-testid="save-lot">Kaydet</Btn></div>
      </Modal>

      {/* iter144 — Raf Ömrü %50+ Uyarı Modalı (Satın Alma / Malzeme Kabul) */}
      <Modal open={!!shelfLifeModal} onClose={() => setShelfLifeModal(null)} title="Raf Ömrü Uyarısı" size="md">
        {shelfLifeModal && (
          <div className="space-y-3" data-testid="shelf-life-warning-modal">
            <div className="p-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-500/40">
              <div className="text-sm font-bold text-amber-900 dark:text-amber-200 mb-1">⚠ Uzun raf ömrü tüketilmiş malzeme</div>
              <div className="text-xs text-amber-800 dark:text-amber-200">{shelfLifeModal.message}</div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="p-2 rounded bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                <div className="text-[10px] uppercase text-slate-500">Kullanılmış Raf Ömrü</div>
                <div className="font-mono font-bold text-rose-700 dark:text-rose-400" data-testid="shelf-life-used-pct">%{shelfLifeModal.shelf_life.used_pct}</div>
              </div>
              <div className="p-2 rounded bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                <div className="text-[10px] uppercase text-slate-500">Kalan Raf Ömrü</div>
                <div className="font-mono font-bold text-emerald-700 dark:text-emerald-400">%{shelfLifeModal.shelf_life.remaining_pct}</div>
              </div>
              <div className="p-2 rounded bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                <div className="text-[10px] uppercase text-slate-500">Toplam Raf Ömrü</div>
                <div className="font-mono">{shelfLifeModal.shelf_life.total_days} gün</div>
              </div>
              <div className="p-2 rounded bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                <div className="text-[10px] uppercase text-slate-500">Kalan Gün</div>
                <div className="font-mono">{shelfLifeModal.shelf_life.remaining_days} gün</div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setShelfLifeModal(null)} data-testid="shelf-life-cancel">Vazgeç</Btn>
              <Btn onClick={() => save(true)} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="shelf-life-accept">Stoğa Kabul Et</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* [iter72] Envanterden Etiket Yazdır — çoklu lot seçim modalı (msg-93 Bölüm B) */}
      <Modal open={!!labelPickMat} onClose={() => setLabelPickMat(null)} title={`Etiket için Lot Seç — ${labelPickMat?.material?.name || ""}`} size="md">
        <div className="text-[11px] text-slate-500 mb-3">
          {(labelPickMat?.lots?.length || 0) > 0
            ? "Bu malzemenin birden fazla lotu var. Etiket basmak istediğiniz lotu seçin."
            : "Bu malzeme için kayıtlı lot yok — etiketi 'Belirlenemedi' alanlarla yine de basabilirsiniz."}
        </div>
        <div className="space-y-1 max-h-80 overflow-y-auto">
          {(labelPickMat?.lots || []).length === 0 ? (
            <Btn size="sm" variant="secondary"
                 onClick={() => { setLabelPreview({ materialId: labelPickMat.material.id, lotId: null, labelKind: "KG" }); setLabelPickMat(null); }}
                 data-testid={`inv-label-nolot-${labelPickMat?.material?.id || ""}`}>
              <Tag size={11}/> Yine de Etiket Yazdır (Lot bilgileri "Belirlenemedi")
            </Btn>
          ) : (labelPickMat?.lots || []).map(lot => (
            <Btn key={lot.id} size="sm" variant="secondary"
                 onClick={() => { setLabelPreview({ materialId: labelPickMat.material.id, lotId: lot.id, labelKind: "KG" }); setLabelPickMat(null); }}
                 data-testid={`inv-label-lot-${lot.id}`}>
              <Tag size={11}/> {lot.lot_code} · Mevcut: {lot.qty_available} · SKT: {(lot.expiry_date||"").slice(0,10) || "—"}
            </Btn>
          ))}
        </div>
      </Modal>

      <BoxLabelPreview
        open={!!labelPreview}
        onClose={() => setLabelPreview(null)}
        source="material"
        materialId={labelPreview?.materialId}
        lotId={labelPreview?.lotId}
        labelKind={labelPreview?.labelKind}
      />

      {/* [iter86] Malzeme lot düzenleme için kamera modal — OCR sonucu runLotOcr'a gider */}
      <CameraCapture
        open={cameraLotId !== null}
        onClose={() => setCameraLotId(null)}
        onCapture={(file) => {
          const id = cameraLotId;
          setCameraLotId(null);
          const lot = matLots.find(l => l.id === id);
          if (lot && file) runLotOcr(lot, file);
        }}
        title="Tedarikçi Etiketi — Fotoğraf Çek"
      />

      {/* iter185 — Hammadde Kartı (yalnız kind=raw_material satırından açılır) */}
      <RawMaterialCardModal
        open={!!rawCardId}
        materialId={rawCardId}
        onClose={() => setRawCardId(null)}
      />

      {/* iter276 — Malzeme Hareket Zaman Çizgisi Modalı (son 10 hareket) */}
      <Modal open={!!movementsModal} onClose={() => setMovementsModal(null)}
             title={`Hareket Zaman Çizgisi — ${movementsModal?.material?.name || ""}`} size="lg">
        {!movementsModal ? null : movementsModal.timeline.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400 italic">Bu malzeme için henüz hareket kaydı yok.</div>
        ) : (
          <div className="space-y-2 max-h-[70vh] overflow-y-auto" data-testid="movements-list">
            <div className="text-[11px] text-slate-500 mb-2 flex items-center gap-2">
              <Clock size={12}/> Son {movementsModal.timeline.length} hareket (en yeni üstte)
              <span className="ml-auto text-slate-400">{movementsModal.material?.code || ""}</span>
            </div>
            {movementsModal.timeline.map((e, i) => {
              const isIn = e.kind === "receipt";
              const isOut = e.kind === "consumption";
              const isRes = e.kind === "reservation";
              const border = isIn ? "border-l-emerald-500 bg-emerald-50/40"
                : isOut ? "border-l-rose-500 bg-rose-50/40"
                : (e.phantom ? "border-l-amber-500 bg-amber-50/40" : "border-l-indigo-500 bg-indigo-50/40");
              const IconEl = isIn ? ArrowDownCircle : isOut ? ArrowUpCircle : Bookmark;
              const iconColor = isIn ? "text-emerald-600" : isOut ? "text-rose-600" : (e.phantom ? "text-amber-600" : "text-indigo-600");
              const label = isIn ? "Alım (Giriş)" : isOut ? "Tüketim (Çıkış)" : (e.phantom ? "Rezerve (Hayali)" : "Rezerve (Gerçek)");
              const qtyPrefix = isIn ? "+" : isOut ? "−" : "»";
              const qtyColor = isIn ? "text-emerald-700" : isOut ? "text-rose-700" : "text-indigo-700";
              return (
                <div key={i} className={`border-l-4 pl-3 pr-2 py-2 rounded ${border}`} data-testid={`movement-${i}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <IconEl size={16} className={iconColor}/>
                      <span className="text-xs font-semibold text-slate-800">{label}</span>
                      {e.phantom && <span className="text-[9px] px-1 rounded bg-amber-200 text-amber-900 uppercase font-bold">Hayali</span>}
                      {e.consumed && isRes && <span className="text-[9px] px-1 rounded bg-slate-200 text-slate-700 uppercase font-bold">Tüketildi</span>}
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono">{(e.ts || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                  <div className="ml-6 mt-1 text-xs text-slate-700 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                    <span className={`font-mono font-bold ${qtyColor}`}>{qtyPrefix} {Number(e.qty || 0).toFixed(2)} {movementsModal.material?.unit || ""}</span>
                    {isIn && e.lot_code && <span className="text-slate-600">Lot: <span className="font-mono text-slate-800">{e.lot_code}</span></span>}
                    {isIn && e.supplier && <span className="text-slate-600">Tedarikçi: <b>{e.supplier}</b></span>}
                    {isIn && e.po_no && <span className="text-slate-600">PO: <span className="font-mono text-teal-700">{e.po_no}</span></span>}
                    {isRes && e.so_no && <Link to="/sales-orders" onClick={() => setMovementsModal(null)} className="text-indigo-700 hover:underline font-mono">{e.so_no}</Link>}
                    {isRes && e.customer_name && <span className="text-slate-600">Müşteri: <b>{e.customer_name}</b></span>}
                    {isRes && e.product_name && <span className="text-slate-600">Ürün: <b>{e.product_name}</b></span>}
                    {isOut && e.prod_order_no && (
                      <Link to={e.prod_order_id ? `/production/${e.prod_order_id}` : "/production"}
                            onClick={() => setMovementsModal(null)}
                            className="text-rose-700 hover:underline font-mono">{e.prod_order_no}</Link>
                    )}
                    {isOut && e.customer_name && <span className="text-slate-600">Müşteri: <b>{e.customer_name}</b></span>}
                    {isOut && e.product_name && <span className="text-slate-600">Ürün: <b>{e.product_name}</b></span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-end mt-3">
          <Btn variant="secondary" onClick={() => setMovementsModal(null)} data-testid="movements-close">Kapat</Btn>
        </div>
      </Modal>

      {/* iter266 — Rezerve Detay Modalı: hangi SO'lardan rezerve düştüğü. Phantom rows ayırt edilir */}
      <Modal open={!!reserveModal} onClose={() => setReserveModal(null)} title={`Rezerve Detayı — ${reserveModal?.material?.name || ""}`} size="lg">
        {reserveModal?.loading && <div className="py-6 text-center text-sm text-slate-500">Yükleniyor...</div>}
        {reserveModal?.error && <div className="p-3 bg-rose-50 border border-rose-200 rounded text-xs text-rose-700">{reserveModal.error}</div>}
        {reserveModal?.totals && (
          <>
            <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
              <div className="p-2 rounded border border-amber-200 bg-amber-50" data-testid="reserve-modal-total">
                <div className="text-[10px] uppercase text-amber-700 font-semibold">Toplam Rezerve</div>
                <div className="font-mono text-base font-bold text-amber-900">{Number(reserveModal.totals.reserved).toFixed(2)} {reserveModal.material?.unit || ""}</div>
              </div>
              <div className="p-2 rounded border border-emerald-200 bg-emerald-50" data-testid="reserve-modal-real">
                <div className="text-[10px] uppercase text-emerald-700 font-semibold">Gerçek (Lot Bağlı)</div>
                <div className="font-mono text-base font-bold text-emerald-800">{Number(reserveModal.totals.real).toFixed(2)}</div>
              </div>
              <div className="p-2 rounded border border-rose-200 bg-rose-50" data-testid="reserve-modal-phantom">
                <div className="text-[10px] uppercase text-rose-700 font-semibold">⚠ Hayali (Fiziki Karşılıksız)</div>
                <div className="font-mono text-base font-bold text-rose-800">{Number(reserveModal.totals.phantom).toFixed(2)}</div>
              </div>
            </div>
            <div className="text-[11px] text-slate-500 mb-2">
              {reserveModal.totals.so_count} satış siparişinden rezerve düşüşü. <b>Hayali (⚠)</b> rezerveler stok o an yeterli olmadığı için oluşan "üzerine yazılmış" taleplerdir — malzeme geldikçe otomatik gerçekleşir.
            </div>
            <div className="overflow-y-auto max-h-96 border border-slate-200 rounded">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-slate-600">SO No</th>
                    <th className="text-left px-3 py-2 font-semibold text-slate-600">Müşteri / Ürün</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-600">Gerçek</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-600">⚠ Hayali</th>
                    <th className="text-right px-3 py-2 font-semibold text-slate-600">Toplam</th>
                  </tr>
                </thead>
                <tbody>
                  {reserveModal.groups.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-slate-400 italic">Aktif rezerve yok.</td></tr>}
                  {reserveModal.groups.map((g, i) => {
                    const total = (g.real_qty || 0) + (g.phantom_qty || 0);
                    return (
                      <tr key={g.sales_order_id || i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`reserve-so-row-${i}`}>
                        <td className="px-3 py-2 font-mono text-indigo-700">{g.so_no || <span className="text-slate-400 italic">SO yok</span>}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-800">{g.customer_name || "—"}</div>
                          <div className="text-[10px] text-slate-500">{g.product_name || ""}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-700">{(g.real_qty || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right font-mono text-rose-700">{g.phantom_qty > 0 ? `⚠ ${g.phantom_qty.toFixed(2)}` : "—"}</td>
                        <td className="px-3 py-2 text-right font-mono font-semibold text-amber-800">{total.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="flex justify-end mt-4">
          <Btn variant="secondary" onClick={() => setReserveModal(null)} data-testid="reserve-modal-close">Kapat</Btn>
        </div>
      </Modal>
    </div>
  );
}
